import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, ShieldCheck, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const HERO = "https://images.unsplash.com/photo-1691689761290-2641cf0fc59a?crop=entropy&cs=srgb&fm=jpg&q=85";

const CATEGORIES = [
  { name: "Hoodies", img: "https://images.unsplash.com/photo-1652823780977-b22c0ed84c97?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { name: "Oversized Tees", img: "https://images.unsplash.com/photo-1616006897093-5e4635c0de35?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { name: "Cargo Pants", img: "https://images.unsplash.com/photo-1660486044177-45cd45bb5e99?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { name: "Outerwear", img: "https://images.unsplash.com/photo-1591047139829-d91aecb6caea?crop=entropy&cs=srgb&fm=jpg&q=85" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 8))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-[88vh] min-h-[560px] w-full overflow-hidden">
        <img src={HERO} alt="Laze drop 004" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/20" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex flex-col justify-end pb-16 sm:pb-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-block bg-brand text-black font-mono text-[11px] font-bold uppercase tracking-widest px-3 py-1 mb-5">
              Drop 004 · Live Now
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold uppercase tracking-tight leading-none max-w-3xl">
              Unfazed.<br />Minimal.<br /><span className="text-brand">Streetwear.</span>
            </h1>
            <p className="mt-5 max-w-md text-sm md:text-base text-zinc-300">
              Heavyweight fabrics, boxy silhouettes, and small-batch drops. Built to be lived in — not looked at.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/shop" data-testid="hero-shop-btn" className="group inline-flex items-center gap-2 rounded-full bg-brand text-black px-7 py-3 text-sm font-bold uppercase tracking-wide hover:bg-brand-hover active:scale-95 transition">
                Shop the Collection <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/shop?category=Outerwear" className="inline-flex items-center rounded-full border border-white/40 px-7 py-3 text-sm font-semibold uppercase tracking-wide hover:border-brand hover:text-brand transition">
                View Outerwear
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-zinc-900">
          {[
            { icon: Truck, t: "Free Shipping", s: "On orders over ₹2,999" },
            { icon: ShieldCheck, t: "Secure UPI Pay", s: "Instant QR checkout" },
            { icon: RotateCcw, t: "7-Day Returns", s: "No-questions exchange" },
          ].map((f) => (
            <div key={f.t} className="flex items-center gap-3 py-5 sm:px-6">
              <f.icon className="h-5 w-5 text-brand" />
              <div>
                <p className="text-sm font-semibold">{f.t}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{f.s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-24">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight">Shop by Category</h2>
          <Link to="/shop" className="font-mono text-xs uppercase tracking-widest text-zinc-400 hover:text-brand">View All →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {CATEGORIES.map((c, i) => (
            <motion.div key={c.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
              <Link to={`/shop?category=${encodeURIComponent(c.name)}`} data-testid={`category-${c.name.toLowerCase().replace(/\s+/g, "-")}`} className="group relative block aspect-[4/5] overflow-hidden bg-zinc-900">
                <img src={c.img} alt={c.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <span className="absolute bottom-4 left-4 font-display text-lg font-bold uppercase tracking-tight group-hover:text-brand transition-colors">{c.name}</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="font-mono text-[11px] uppercase tracking-widest text-brand">Curated</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight mt-1">Featured Drops</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-8 sm:gap-x-4">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>
    </div>
  );
}
